import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { Search } from "lucide-react";
import CustomPagination from "../common/CustomPagination.jsx";
import { getHandoverHistoryApi } from "./handover-api";

const formatUserDisplay = (val) => {
  if (!val) return "-";
  const str = String(val).trim();
  const angleMatch = /^([^<]+)<([^>]+)>/.exec(str);
  if (angleMatch) {
    const name = angleMatch[1].trim();
    const email = angleMatch[2].trim();
    if (name && email) return `${name} - ${email}`;
    return name || email || "-";
  }

  const parts = str.split(/\s+/);
  const emailPart = parts.find((p) => p.includes("@"));
  if (emailPart) {
    const cleanEmail = emailPart.replace(/[<>]/g, "").trim();
    const cleanName = parts.filter((p) => !p.includes("@")).join(" ").replace(/[<>]/g, "").trim();
    if (cleanName && cleanEmail) return `${cleanName} - ${cleanEmail}`;
    return cleanName || cleanEmail || "-";
  }

  return str.replace(/[<>]/g, "").trim() || "-";
};

const HandoverHistory = ({ onSelectLog, recordsPerPage = 10 }) => {
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historySearch, setHistorySearch] = useState("");
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalRecords, setHistoryTotalRecords] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const skip = (historyPage - 1) * recordsPerPage;
      const res = await getHandoverHistoryApi({
        search: historySearch,
        limit: recordsPerPage,
        skip: skip,
      });
      if (res?.data?.success) {
        setHistoryLogs(res.data.history || []);
        setHistoryTotalRecords(res.data.total_rec || 0);
      } else {
        setHistoryLogs([]);
        setHistoryTotalRecords(0);
      }
    } catch (err) {
      console.error("Error fetching handover history:", err);
      setHistoryLogs([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [historySearch, historyPage, recordsPerPage]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const renderHistoryContent = () => {
    if (historyLoading) {
      return (
        <tr>
          <td colSpan="6" className="text-center py-5">
            <div className="d-flex flex-column align-items-center justify-content-center gap-3 py-3">
              <Spinner animation="border" style={{ color: "#6929e2", width: "2.2rem", height: "2.2rem", borderWidth: "3px" }} />
              <span style={{ fontSize: "14px", color: "#697386", fontWeight: "500" }}>Loading history logs...</span>
            </div>
          </td>
        </tr>
      );
    }
    if (historyLogs.length === 0) {
      return (
        <tr>
          <td colSpan="6" className="text-center py-4 text-muted">
            No handover history recorded yet.
          </td>
        </tr>
      );
    }
    return historyLogs.map((log) => (
      <tr key={log.id} style={{ cursor: "pointer" }} onClick={() => onSelectLog(log)}>
        <td style={{ fontWeight: "600", color: "#1a1f36" }}>{formatUserDisplay(log.from_user)}</td>
        <td style={{ fontWeight: "600", color: "#1a1f36" }}>{formatUserDisplay(log.to_user)}</td>
        <td>
          <span className="badge bg-purple text-white px-2 py-1" style={{ backgroundColor: "#6929e2" }}>
            {log.ticket_count || (log.tickets ? log.tickets.length : 0)} tickets
          </span>
        </td>
        <td>{formatUserDisplay(log.admin_user || log.created_by)}</td>
        <td style={{ maxWidth: "250px", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
          {log.remarks || "-"}
        </td>
        <td>{log.date_time || log.date || "-"}</td>
      </tr>
    ));
  };

  return (
    <div className="handover-table-card">
      <div className="handover-table-toolbar">
        <h5 className="m-0 font-weight-bold" style={{ fontSize: "16px", color: "#1a1f36" }}>Handover History Logs</h5>
        <div className="search-input-wrapper">
          <Search className="search-icon" size={15} />
          <input
            type="text"
            placeholder="Search history..."
            value={historySearch}
            onChange={(e) => {
              setHistorySearch(e.target.value);
              setHistoryPage(1);
            }}
          />
        </div>
      </div>

      <div className="handover-table-responsive">
        <table className="handover-table">
          <thead>
            <tr>
              <th>From user</th>
              <th>To User</th>
              <th>Total Tickets</th>
              <th>Handover by</th>
              <th>Remarks</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>{renderHistoryContent()}</tbody>
        </table>
      </div>

      
      <CustomPagination
        currentPage={historyPage}
        totalRecords={historyTotalRecords}
        recordsPerPage={recordsPerPage}
        onPageChange={setHistoryPage}
        labelText="Total Logs"
      />
    </div>
  );
};

export default HandoverHistory;
